import { Dialog } from '@/components/shared/components/Dialog';
import { FormattedText } from '@/components/shared/components/FormattedText';
import { PillButton } from '@/components/shared/components/Pill';
import { debounce } from 'es-toolkit';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PlaceholderTextarea } from './PlaceholderTextarea';
import {
    DialogBody,
    ErrorText,
    FormatHelpLink,
    FieldWrapper,
    FormActions,
    BodyPreviewRow,
    Label,
    PlaceholderList,
    PreviewBox,
    PreviewColumn,
    PreviewEmpty,
    PreviewSpinner,
} from './TemplateFormDialog.StyledComponents';
import {
    BaseTemplate,
    ExtraFieldsConfig,
    TemplateFormValues,
    TemplatePreviewResult,
} from './TemplateManager.types';

const PREVIEW_DEBOUNCE_MS = 1000;
// Wide enough for Body and Preview side by side; Dialog.css still caps it at 80vw.
const DIALOG_STYLE = { width: '1000px' };
const FORMATTING_HELP_URL =
    'https://docs.github.com/en/get-started/writing-on-github/getting-started-with-writing-and-formatting-on-github/basic-writing-and-formatting-syntax';

interface TemplateFormDialogProps<T extends BaseTemplate, TExtra extends object> {
    visible: boolean;
    onHide: () => void;
    editingItem: T | null;
    placeholders: string[];
    onPreview: (body: string, editingItem: T | null) => Promise<TemplatePreviewResult>;
    onCreate: (values: TemplateFormValues & TExtra) => Promise<T>;
    onUpdate: (id: string, values: TemplateFormValues & TExtra) => Promise<T>;
    onSaved: (item: T) => void;
    extraFields?: ExtraFieldsConfig<T, TExtra>;
}

const errorMessageFrom = (error: unknown): string =>
    error instanceof Error ? error.message : 'Something went wrong. Please try again.';

export const TemplateFormDialog = <T extends BaseTemplate, TExtra extends object>({
    visible,
    onHide,
    editingItem,
    placeholders,
    onPreview,
    onCreate,
    onUpdate,
    onSaved,
    extraFields,
}: TemplateFormDialogProps<T, TExtra>) => {
    const [label, setLabel] = useState('');
    const [body, setBody] = useState('');
    const [extraValues, setExtraValues] = useState<TExtra>(
        () => extraFields?.getInitialValues(null) ?? ({} as TExtra),
    );
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [preview, setPreview] = useState<TemplatePreviewResult | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const previewRequestIdRef = useRef(0);
    const bodyRef = useRef<HTMLTextAreaElement>(null);
    const pendingCaretRef = useRef<number | null>(null);

    useEffect(() => {
        if (visible) {
            setLabel(editingItem?.label ?? '');
            setBody(editingItem?.body ?? '');
            setExtraValues(extraFields?.getInitialValues(editingItem) ?? ({} as TExtra));
            setError(null);
            setPreview(null);
        }
        // extraFields is a config object the caller re-creates each render; only visible/editingItem should retrigger this.
    }, [visible, editingItem]);

    const debouncedPreview = useMemo(
        () =>
            debounce(async (bodyToPreview: string, item: T | null, requestId: number) => {
                try {
                    const result = await onPreview(bodyToPreview, item);
                    if (requestId === previewRequestIdRef.current) {
                        setPreview(result);
                    }
                } catch {
                    if (requestId === previewRequestIdRef.current) {
                        setPreview(null);
                    }
                } finally {
                    if (requestId === previewRequestIdRef.current) {
                        setIsPreviewLoading(false);
                    }
                }
            }, PREVIEW_DEBOUNCE_MS),
        [onPreview],
    );

    useEffect(() => {
        if (!visible || !body.trim()) {
            setPreview(null);
            setIsPreviewLoading(false);
            return;
        }

        previewRequestIdRef.current += 1;
        setIsPreviewLoading(true);
        debouncedPreview(body, editingItem, previewRequestIdRef.current);

        return () => debouncedPreview.cancel();
    }, [visible, body, editingItem, debouncedPreview]);

    // Runs after the inserted text is in the DOM, so the caret lands just past the new token.
    useLayoutEffect(() => {
        const caret = pendingCaretRef.current;
        if (caret === null || !bodyRef.current) return;
        pendingCaretRef.current = null;
        bodyRef.current.focus();
        bodyRef.current.setSelectionRange(caret, caret);
    }, [body]);

    const insertPlaceholder = (placeholder: string) => {
        const textarea = bodyRef.current;
        const token = `[[${placeholder}]]`;
        // A badge click keeps focus in the body (see onMouseDown), so an unfocused body means there's no caret to honour.
        const hasCaret = textarea !== null && document.activeElement === textarea;
        const start = hasCaret ? textarea.selectionStart : body.length;
        const end = hasCaret ? textarea.selectionEnd : body.length;
        pendingCaretRef.current = start + token.length;
        setBody(body.slice(0, start) + token + body.slice(end));
    };

    const handleSave = async () => {
        if (!label.trim() || !body.trim()) {
            setError('Label and Body are both required.');
            return;
        }
        const extraFieldsError = extraFields?.validate?.(extraValues) ?? null;
        if (extraFieldsError) {
            setError(extraFieldsError);
            return;
        }

        setIsSaving(true);
        setError(null);
        try {
            const values = {
                label: label.trim(),
                body: body.trim(),
                ...extraValues,
            } as TemplateFormValues & TExtra;
            const saved = editingItem
                ? await onUpdate(editingItem.id, values)
                : await onCreate(values);
            setIsSaving(false);
            onSaved(saved);
        } catch (err) {
            setIsSaving(false);
            setError(errorMessageFrom(err));
        }
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={editingItem ? 'Edit Template' : 'New Template'}
            data-testid="template-form-dialog"
            appendTo="self"
            style={DIALOG_STYLE}
            // Only Cancel or Save may close it, so a stray click or Escape can't throw away an edit.
            dismissableMask={false}
            closable={false}
            closeOnEscape={false}
        >
            <DialogBody>
                <FieldWrapper>
                    <Label htmlFor="template-form-label">Label</Label>
                    <InputText
                        id="template-form-label"
                        data-testid="template-form-label"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                    />
                </FieldWrapper>

                {editingItem && extraFields?.renderDetails
                    ? extraFields.renderDetails(editingItem)
                    : null}

                {placeholders.length > 0 ? (
                    <FieldWrapper>
                        <Label>Placeholders available</Label>
                        <PlaceholderList>
                            {placeholders.map((placeholder) => (
                                <PillButton
                                    key={placeholder}
                                    type="button"
                                    variant="grey"
                                    title="Insert at cursor"
                                    data-testid={`template-form-placeholder-${placeholder}`}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => insertPlaceholder(placeholder)}
                                >
                                    {`[[${placeholder}]]`}
                                </PillButton>
                            ))}
                        </PlaceholderList>
                    </FieldWrapper>
                ) : null}

                <BodyPreviewRow>
                    <FieldWrapper>
                        <Label htmlFor="template-form-body">Body</Label>
                        <PlaceholderTextarea
                            ref={bodyRef}
                            id="template-form-body"
                            data-testid="template-form-body"
                            value={body}
                            onChange={setBody}
                            placeholders={placeholders}
                            rows={6}
                        />
                    </FieldWrapper>

                    <PreviewColumn>
                        <Label>
                            Preview
                            <FormatHelpLink
                                href={FORMATTING_HELP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="template-form-format-help"
                            >
                                (How to format the text?)
                            </FormatHelpLink>
                            {isPreviewLoading ? (
                                <PreviewSpinner
                                    data-testid="template-form-preview-loading"
                                    strokeWidth="4"
                                />
                            ) : null}
                        </Label>
                        {preview !== null ? (
                            <>
                                <PreviewBox data-testid="template-form-preview">
                                    <FormattedText>{preview.renderedText}</FormattedText>
                                </PreviewBox>
                                {preview.validationErrors.map((validationError) => (
                                    <ErrorText
                                        key={validationError}
                                        data-testid="template-form-preview-error"
                                    >
                                        {validationError}
                                    </ErrorText>
                                ))}
                            </>
                        ) : (
                            <PreviewBox data-testid="template-form-preview-empty">
                                <PreviewEmpty>
                                    {isPreviewLoading ? 'Loading preview…' : 'Type a body to see a preview.'}
                                </PreviewEmpty>
                            </PreviewBox>
                        )}
                    </PreviewColumn>
                </BodyPreviewRow>

                {extraFields ? extraFields.render(extraValues, setExtraValues, editingItem) : null}

                {error ? <ErrorText data-testid="template-form-error">{error}</ErrorText> : null}

                <FormActions>
                    <Button
                        label="Cancel"
                        text
                        type="button"
                        data-testid="template-form-cancel"
                        onClick={onHide}
                    />
                    <Button
                        label="Save"
                        type="button"
                        data-testid="template-form-save"
                        disabled={isSaving}
                        onClick={handleSave}
                    />
                </FormActions>
            </DialogBody>
        </Dialog>
    );
};
